import React from 'react';
import { Choir } from '../../types/Choir';
import { Users, Music } from 'lucide-react';
import { TableColumn } from '../../utils/tableUtils';
import { StatusBadge } from '../shared/AdminTable';

// Define standardized choir table columns
export const choirColumns: TableColumn<Choir>[] = [
  {
    id: 'name',
    label: 'Choir Name',
    accessor: (choir) => choir.name,
    sortable: true,
  },
  {
    id: 'description',
    label: 'Description',
    accessor: (choir) => (
      <div className="max-w-xs truncate" title={choir.description}>
        {choir.description}
      </div>
    ),
    sortable: true,
  },
  {
    id: 'memberCount',
    label: 'Members',
    accessor: (choir) => (
      <div className="flex items-center gap-1">
        <Users size={16} className="text-gray-400" />
        <span>{choir.memberCount || 0}</span>
      </div>
    ),
    sortable: true,
  },
  {
    id: 'skillLevel',
    label: 'Skill Level',
    accessor: (choir) => choir.skillLevel ? (
      <StatusBadge
        status={choir.skillLevel}
        type="skillLevel"
      />
    ) : (
      <span className="text-gray-400">Not specified</span>
    ),
    sortable: true,
  },
  {
    id: 'auditionRequired',
    label: 'Audition',
    accessor: (choir) => (
      <StatusBadge
        status={choir.auditionRequired ? 'Required' : 'Not Required'}
        type="audition"
      />
    ),
    sortable: true,
  },
  {
    id: 'category',
    label: 'Category',
    accessor: (choir) => (
      <div className="flex items-center gap-1">
        <Music size={16} className="text-gray-400" />
        <span>{choir.category || 'General'}</span>
      </div>
    ),
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    accessor: (choir) => (
      <StatusBadge
        status={choir.isActive ? 'Active' : 'Inactive'}
        type="active"
      />
    ),
    sortable: true,
  },
  {
    id: 'lastUpdated',
    label: 'Last Updated',
    accessor: (choir) => new Date(choir.updatedAt).toLocaleDateString(),
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: () => null, // Actions are handled by the AdminTable component
    sortable: false,
  },
];

// Choir table configuration
export const choirTableConfig = {
  entityName: 'Choir',
  apiEndpoint: '/api/admin/choirs',
  columns: choirColumns,
  searchFields: ['name', 'description', 'category'] as (keyof Choir)[],
  filterOptions: {
    status: [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' }
    ],
    skillLevel: [
      { value: 'Beginner-Friendly', label: 'Beginner-Friendly' },
      { value: 'Intermediate', label: 'Intermediate' },
      { value: 'Advanced', label: 'Advanced' }
    ],
    auditionRequired: [
      { value: 'Required', label: 'Audition Required' },
      { value: 'Not Required', label: 'No Audition Required' }
    ],
    category: [
      { value: 'Mixed', label: 'Mixed Choir' },
      { value: 'Women\'s', label: 'Women\'s Choir' },
      { value: 'Men\'s', label: 'Men\'s Choir' },
      { value: 'Chamber', label: 'Chamber Choir' },
      { value: 'Show', label: 'Show Choir' }
    ]
  },
  exportFileName: 'choirs',
  defaultVisibleColumns: {
    name: true,
    description: true,
    memberCount: true,
    skillLevel: true,
    auditionRequired: true,
    category: true,
    status: true,
    lastUpdated: true,
    actions: true
  }
};
